import { useMemo } from "react"

import {
  useFacet,
  useFacetRegistration,
  usePagination,
} from "@/lib/redux-search"
import type { Bucket, FacetConfig } from "@/lib/redux-search/store/facets-slice"

export type TermFacetOption = {
  value: string
  label: string
  count: number
  selected: boolean
  description?: string
  searchTerms?: string[]
}

/**
 * A registered term facet described for rendering: what to call it, its
 * options with their selection state, and how to change the selection.
 * Selected options come first in selection order and are retained when a
 * response omits their buckets; the rest follow by descending count.
 */
export type TermFacetDescriptor = {
  id: string
  label: string
  pillLabel: (value: string) => string
  searchable: boolean
  options: TermFacetOption[]
  toggle: (value: string) => void
  clear: () => void
}

export type TermFacetDefinition = {
  id: string
  config: FacetConfig
  label: string
  pillLabel: (value: string) => string
  searchable?: boolean
  option: (value: string, count: number) => Omit<TermFacetOption, "selected">
}

const NO_BUCKETS: Bucket[] = []
const NO_FILTERS: string[] = []

/** Registers a route-local Redux term facet and describes it. */
export function useTermFacetDescriptor({
  id,
  config,
  label,
  pillLabel,
  searchable = false,
  option,
}: TermFacetDefinition): TermFacetDescriptor {
  useFacetRegistration(id, config, {
    displayLabel: label,
    displayValue: pillLabel,
  })
  const facet = useFacet(id)
  const pagination = usePagination()
  const buckets = facet.isRegistered ? facet.buckets : NO_BUCKETS
  const filters = facet.isRegistered ? facet.filters : NO_FILTERS

  const options = useMemo(() => {
    const available = buckets.map(({ name, count }) => option(name, count))
    const selected = filters.map((value) => ({
      ...(available.find((item) => item.value === value) ?? option(value, 0)),
      selected: true,
    }))
    const unselected = available
      .filter((item) => !filters.includes(item.value))
      .sort((a, b) => b.count - a.count)
      .map((item) => ({ ...item, selected: false }))
    return [...selected, ...unselected]
  }, [buckets, filters, option])

  return useMemo(() => {
    const search = () => {
      pagination.setPage(1)
      if (facet.isRegistered) facet.search()
    }
    return {
      id,
      label,
      pillLabel,
      searchable,
      options,
      toggle(value: string) {
        if (facet.isRegistered) facet.toggleFilter(value)
        search()
      },
      clear() {
        if (facet.isRegistered) facet.clearFilters()
        search()
      },
    }
  }, [id, label, pillLabel, searchable, options, facet, pagination])
}
